import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { FeatureTypes } from "entity-app/constants";
import { DateUtils } from "utils/dateutils";
import { ContextColumnConfigType } from "../models";
import { LINKED_SUBMITTALS_KEY } from "../custom-reports-constants";

const headerValueGetter = (
  params: any,
  fieldKey: string,
  parentKey?: string
) => {
  try {
    if (!params?.context?.columnConfig) {
      return "";
    }
    let fields = params?.context?.columnConfig;

    if (parentKey) {
      fields = fields?.find(
        (column: any) => column.field_key === parentKey
      )?.fields;
    }

    const foundColumn = fields?.find(
      (column: any) => column.field_key === fieldKey
    );
    return foundColumn?.caption_en || fieldKey;
  } catch (err) {
    console.log("Error ", fieldKey, err);
  }
  return "";
};

const flattenData = (data: any, inlineModeForFeatures: number[]) => {
  if (!data) return [];
  return data.flatMap((item: any) => {
    const linkedSubmittals = item.linked_submittals || [];
    const linkedActivities = item.linked_activities || [];

    const lengths = [];

    if (inlineModeForFeatures.includes(FeatureTypes.SUBMITTALS)) {
      lengths.push(linkedSubmittals.length);
    }

    if (inlineModeForFeatures.includes(FeatureTypes.ACTIVITIES)) {
      lengths.push(linkedActivities.length);
    }

    const maxLength = lengths.length > 0 ? Math.max(...lengths) : 0;

    if (maxLength === 0) {
      return [item];
    }

    return Array.from({ length: maxLength }, (__, i) => ({
      ...item,
      shodowRow: i !== 0,
      ...linkedSubmittals[i],
      ...linkedActivities[i]
    }));
  });
};

const shouldSpanRow = ({
  path,
  nodeAData,
  nodeBData
}: {
  path: string;
  nodeAData: any;
  nodeBData: any;
}) => {
  if (nodeAData.id !== nodeBData.id) return false;

  if (path === "linked_activities") {
    const activityAIds = _.get(nodeAData, path)?.map(
      (activity: any) => activity.id
    );
    const activityBIds = _.get(nodeBData, path)?.map(
      (activity: any) => activity.id
    );
    return _.isEqual(activityAIds, activityBIds);
  }

  return _.get(nodeAData, path) === _.get(nodeBData, path);
};

// Update visibility in columnConfig
const updateColumnVisibility = (
  config: ContextColumnConfigType[],
  fieldKey: string,
  visible: boolean
): any[] =>
  config.map((col) => {
    if (col.field_key === fieldKey) {
      return { ...col, visibility: visible };
    }
    if (col.fields) {
      return {
        ...col,
        fields: updateColumnVisibility(col.fields, fieldKey, visible)
      };
    }
    return col;
  });

// Update order in columnConfig
const updateColumnOrder = (
  config: ContextColumnConfigType[],
  orderedKeys: string[]
): any[] => {
  return config
    .map((col) => {
      if (col.fields) {
        return {
          ...col,
          fields: updateColumnOrder(col.fields, orderedKeys),
          order: orderedKeys.indexOf(col.field_key) + 1
        };
      }
      return { ...col, order: orderedKeys.indexOf(col.field_key) + 1 };
    })
    .sort((a, b) => a.order - b.order);
};

const translateVLargeMaterial = (materials: Array<any>) => {
  return materials.map((m) => {
    // const { length } = m.milestones;
    // const firstMilestone = m.milestones?.find((x: any) => x.sequence_no === 1);
    // const lastMilestone = m.milestones?.find(
    //   (x: any) => x.sequence_no === length
    // );

    return {
      ...m,
      planned_first_milestone_date: m.planned_milestone_1,
      actual_first_milestone_date: m.actual_milestone_1,
      actual_last_milestone_date: m.actual_last_milestone_date,
      project_locations: m.project_locations?.map((loc: string) => ({
        id: loc,
        name: loc,
        path: loc
      })),
      tasks:
        m.linked_activities?.map((t: any) => ({
          task_id: t.linked_activities__id,
          task_name: t.linked_activities__name,
          offset_duration: t.linked_activities__offset_duration,
          linked_offset_date: t.linked_activities__linked_offset_date,
          activity_id: t.linked_activities__number,
          critical: t.linked_activities__is_critical,
          total_slack: t.linked_activities__total_slack,
          is_critical: t.linked_activities__is_critical,
          total_slack_computed: null,
          progress: t.linked_activities__progress,
          start_date: t.linked_activities__start_date,
          end_date: t.linked_activities__end_date,
          linked_to_end_date: t.linked_activities__linked_to_end_date,
          actual_start_date: t.linked_activities__start_date,
          actual_finish_date: t.linked_activities__end_date
        })) || [],

      linked_items: m.linked_submittals?.map((s: any) => ({
        ...s,

        id: s.linked_submittals__id,
        feature_type: 1,
        sequence_id: s.linked_submittals__sequence_id,
        name: s.linked_submittals__title,
        milestone_state: s.linked_submittals__status,
        risk_level: s.linked_submittals__risk,
        spec_section: s.linked_submittals__spec_sections,
        milestones: s.linked_submittals__milestones?.map((x: any) => ({
          actual_date: x.actual,
          name: x.name,
          projected_date: x.projected,
          planned_date: x.planned,
          sequence_no: x.sequence_no,
          milestone_action: x.milestone_action,
          is_active: x.is_active
        })),
        revision: s.linked_submittals__revision,
        deleted_from_source: false,
        workflow_status: s.linked_submittals__workflow_status,
        type: s.linked_submittals__type
      }))
    };
  });
};

const reorderJSON1FromJSON2 = (
  json1: any,
  json2: any,
  viewMode: string | undefined
) => {
  const nestedMap: any = {
    linked_submittals: `${LINKED_SUBMITTALS_KEY}__`
    // linked_activities: "activity__"
  };

  const rootMap = new Map();
  const nestedCols: any = {};

  // Separate root fields and locate nested blocks
  json1.forEach((col: any) => {
    if (viewMode === "inline" && col.fields && nestedMap[col.field_key]) {
      nestedCols[col.field_key] = _.cloneDeep(col); // clone nested block
    } else {
      rootMap.set(col.field_key, { ...col });
    }
  });

  const result: any[] = [];
  let newOrder = 1;
  const addedRootKeys = new Set();

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < json2.length; i++) {
    // eslint-disable-line no-plusplus
    const key = json2[i];

    // Check if this key belongs to any nested block
    const nestedEntry = Object.entries(nestedMap).find(([, prefix]) =>
      key.startsWith(prefix)
    );

    if (nestedEntry) {
      const [nestedKey, prefix] = nestedEntry;
      const nestedBlock = nestedCols[nestedKey];
      if (!nestedBlock) continue; // eslint-disable-line no-continue

      // Collect all consecutive keys of this nested type
      const tempSubFields: any[] = [];
      while (i < json2.length && json2[i].startsWith(prefix)) {
        const fullKey = json2[i]; // Use full key - field_key in nestedBlock.fields includes the prefix
        const subCol = nestedBlock.fields.find(
          (f: any) => f.field_key === fullKey
        );
        if (subCol) tempSubFields.push({ ...subCol });
        i++; // eslint-disable-line no-plusplus
      }
      // eslint-disable-next-line no-plusplus
      i--; // adjust for outer loop increment

      // Reassign order
      // eslint-disable-next-line no-return-assign, no-param-reassign
      tempSubFields.forEach((f: any, idx: number) => (f.order = idx + 1));
      nestedBlock.fields = tempSubFields;
      nestedBlock.order = newOrder++; // eslint-disable-line no-plusplus
      result.push(nestedBlock);
    } else {
      const col = rootMap.get(key);
      if (col && !addedRootKeys.has(col.field_key)) {
        col.order = newOrder++; // eslint-disable-line no-plusplus
        result.push(col);
        addedRootKeys.add(col.field_key);
      }
    }
  }

  // Append remaining root fields not in JSON-2
  rootMap.forEach((col: any) => {
    if (!addedRootKeys.has(col.field_key)) {
      // eslint-disable-next-line no-param-reassign
      col.order = newOrder++; // eslint-disable-line no-plusplus
      result.push(col);
    }
  });

  // Final order recalculation for nested fields
  result.forEach((col: any, idx: number) => {
    // eslint-disable-next-line no-param-reassign
    col.order = idx + 1;
    if (col.fields) {
      col.fields.forEach(
        // eslint-disable-next-line no-return-assign, no-param-reassign
        (subCol: any, subIdx: number) => (subCol.order = subIdx + 1)
      );
    }
  });

  return result;
};

const flattenJSON1ToJSON2 = (json1: any, viewMode: string | undefined) => {
  if (!viewMode) return [];

  const nestedMap: any = {
    linked_submittals: `${LINKED_SUBMITTALS_KEY}__`
    // linked_activities: "activity__"
  };

  const result: any[] = [];
  let currentOrder = 1;

  json1.forEach((col: any) => {
    if (viewMode === "inline" && col.fields && nestedMap[col.field_key]) {
      col.fields.forEach((subCol: any) => {
        result.push({
          colId: subCol.field_key, // Use field_key directly - it already includes the prefix
          hide: !subCol.visibility,
          order: currentOrder++ // eslint-disable-line no-plusplus
        });
      });
    } else {
      result.push({
        colId: col.field_key,
        hide: !col.visibility,
        order: currentOrder++ // eslint-disable-line no-plusplus
      });
    }
  });

  return result;
};

const applyUnassignedDefaults = (
  materials: any[],
  groupByField: string | undefined,
  columnConfig: any[] | null
) => {
  if (!groupByField) return materials;

  const uuidForGCRepresentative = uuidv4();
  const uuidForResponsibleContractor = uuidv4();
  const uuidForAssigneeUser = uuidv4();

  const fieldConfig: Record<
    string,
    {
      defaultLabel: string;
      applyDefault: (item: any, unassignedLabel: string) => any;
    }
  > = {
    gc_representative: {
      defaultLabel: "GC Representative",
      applyDefault: (item, label) => ({
        ...item,
        gc_representative: item.gc_representative || {
          id: uuidForGCRepresentative,
          name: `Unassigned ${label}`,
          email: ""
        }
      })
    },
    responsible_contractor_name: {
      defaultLabel: "Responsible Contractor",
      applyDefault: (item, label) => ({
        ...item,
        responsible_contractor_id:
          item.responsible_contractor_id || uuidForResponsibleContractor,
        responsible_contractor_name:
          item.responsible_contractor_name || `Unassigned ${label}`
      })
    },
    assignee_user: {
      defaultLabel: "Assignee User",
      applyDefault: (item, label) => ({
        ...item,
        assignee_user: item.assignee_user || {
          id: uuidForAssigneeUser,
          name: `Unassigned ${label}`,
          email: ""
        }
      })
    },
    spec_sections: {
      defaultLabel: "Spec Section",
      applyDefault: (item) => ({
        ...item,
        spec_sections: item.spec_sections || ["Unassigned Spec Section"]
      })
    }
  };

  const config = fieldConfig[groupByField];
  if (!config) return materials;

  const columnHeader =
    columnConfig?.find((col: any) => col.field_key === groupByField)
      ?.caption_en || config.defaultLabel;

  return materials.map((item: any) => config.applyDefault(item, columnHeader));
};

const dateCellValueGetter = (params: any) => {
  const dateValue = params.data?.[params.colDef.field as string];
  return dateValue ? DateUtils.format(dateValue) : undefined;
};

const pairStringsInArray = ({
  startString,
  pairs,
  inputArray
}: {
  startString: string;
  pairs: string[];
  inputArray: string[];
}) => {
  const clonedInputArray = [...inputArray];
  // remove the pairs from the clonedInputArray
  pairs.forEach((pair) => {
    const index = clonedInputArray.indexOf(pair);
    if (index !== -1) clonedInputArray.splice(index, 1);
  });
  // find the position of the startString in the clonedInputArray
  const startIndex = clonedInputArray.indexOf(startString);
  // insert the pairs after the startString
  clonedInputArray.splice(startIndex + 1, 0, ...pairs);
  return clonedInputArray;
};

const updateGridSortConfig = ({
  sortState,
  groupByField,
  groupByOptions,
  columnConfig
}: {
  sortState: { sort: "asc" | "desc" | null; field_key: string }[];
  groupByField: string;
  groupByOptions: string[];
  columnConfig: ContextColumnConfigType[];
}) => {
  let clonedSortState = _.cloneDeep(sortState);
  if (groupByField !== "none") {
    // check if sortState has a sort for any of the groupByOptions
    const hasSort = clonedSortState.some((sort) =>
      groupByOptions.includes(sort.field_key)
    );
    if (hasSort) {
      // remove the sort for any of the groupByOptions
      clonedSortState = clonedSortState.filter(
        (sort) => !groupByOptions.includes(sort.field_key)
      );
      // remove array item with field_key as "group_by_column"
      clonedSortState = clonedSortState.filter(
        (sort) => sort.field_key !== "group_by_column"
      );
    }

    // insert two new items in the clonedSortState at the beginning
    clonedSortState.unshift({
      field_key: groupByField,
      sort: "asc"
    });
    clonedSortState.unshift({
      field_key: "group_by_column",
      sort: "asc"
    });
    return clonedSortState;
  }
  // remove array item with field_key as "group_by_column"
  clonedSortState = clonedSortState.filter(
    (sort) => sort.field_key !== "group_by_column"
  );
  // remove array item with field_key matching any of the groupByOptions
  clonedSortState = clonedSortState.filter(
    (sort) => !groupByOptions.includes(sort.field_key)
  );

  // get item from columnConfig whose order is lowest, ignore the item with field_key as "group_by_column"
  const lowestOrderColumn = [...columnConfig]
    ?.filter(
      (col) =>
        col.field_key !== "group_by_column" && col.field_key !== "description"
    )
    .sort((a, b) => a.order - b.order);

  const columnWithVisibilityEnabled = lowestOrderColumn.find(
    (col) => col.visibility
  );

  if (columnWithVisibilityEnabled) {
    clonedSortState.unshift({
      field_key: columnWithVisibilityEnabled.field_key,
      sort: "asc"
    });
  }

  return clonedSortState;
};

const headerTooltipValueGetter = (params: any) => {
  return params.value || "";
};

export {
  headerValueGetter,
  flattenData,
  shouldSpanRow,
  updateColumnVisibility,
  updateColumnOrder,
  translateVLargeMaterial,
  reorderJSON1FromJSON2,
  flattenJSON1ToJSON2,
  applyUnassignedDefaults,
  dateCellValueGetter,
  pairStringsInArray,
  updateGridSortConfig,
  headerTooltipValueGetter
};
